import { lightTheme, darkTheme } from 'amazon-chime-sdk-component-library-react';

const { colors: lightThemeColors, shadows: lightThemeShadows } = lightTheme;
const { colors: darkThemeColors, shadows: darkThemeShadows } = darkTheme;

const chatLightTheme = {
    title: lightThemeColors.greys.grey100,
    primaryText: lightThemeColors.greys.grey80,
    secondaryText: lightThemeColors.greys.grey50,
    headerBorder: lightThemeColors.greys.grey40,
    containerBorder: lightThemeColors.greys.grey30,
    bgd: lightThemeColors.greys.grey10,
    fgd: lightThemeColors.greys.white,
    shadow: lightThemeShadows.large,
    maxWidth: '20.5rem',
};

const meetingInfoLightTheme = {
    title: lightThemeColors.greys.grey100,
    primaryText: lightThemeColors.greys.grey80,
    secondaryText: lightThemeColors.greys.grey50,
    headerBorder: lightThemeColors.greys.grey40,
    containerBorder: lightThemeColors.greys.grey30,
    bgd: lightThemeColors.greys.grey10,
    fgd: lightThemeColors.greys.white,
    shadow: lightThemeShadows.large,
    maxWidth: '20.5rem',
};

const chatDarkTheme = {
    title: darkThemeColors.greys.white,
    primaryText: darkThemeColors.greys.white,
    secondaryText: darkThemeColors.greys.grey20,
    headerBorder: darkThemeColors.greys.black,
    containerBorder: darkThemeColors.greys.black,
    bgd: darkThemeColors.greys.grey100,
    fgd: darkThemeColors.greys.grey60,
    shadow: darkThemeShadows.large,
    maxWidth: '20.5rem',
};

const meetingInfoDarkTheme = {
    title: darkThemeColors.greys.white,
    primaryText: darkThemeColors.greys.white,
    secondaryText: darkThemeColors.greys.grey20,
    headerBorder: darkThemeColors.greys.black,
    containerBorder: darkThemeColors.greys.black,
    bgd: darkThemeColors.greys.grey100,
    fgd: darkThemeColors.greys.grey60,
    shadow: darkThemeShadows.large,
    maxWidth: '20.5rem',
};

export const demoLightTheme = {
    ...lightTheme,
    chat: chatLightTheme,
    meetingInfo: meetingInfoLightTheme
};

export const demoDarkTheme = {
    ...darkTheme,
    chat: chatDarkTheme,
    meetingInfo: meetingInfoDarkTheme
};

const isValidCSSHex = (hex) => {
    // matches 6 digit characters prefixed with a '#'.
    return /^#[0-9A-F]{6}$/i.test(hex);
};
const hexTorgba = (hex, alpha = 1) => {
    var _a;
    if (!isValidCSSHex(hex)) {
        return '';
    }
    const [r, g, b] = (_a = hex.match(/\w\w/g)) === null || _a === void 0 ? void 0 : _a.map((h) => parseInt(h, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha || 1})`;
};

const colorsVirtualHelp = {
    primary: {
        lightest: '#ACECC8',
        lighter: '#5AD891',
        light: '#39D07A',
        main: '#29B162',
        dark: '#25A75B',
        darker: '#1F844E',
        darkest: '#17633B',
    },
    secondary: {
        light: '#6271D0',
        main: '#2F3E9E',
        dark: '#1C255E',
    },
    error: {
        lightest: '#FCF7F6',
        lighter: '#F5DDD5',
        light: '#FF927C',
        primary: '#C52000',
        dark: '#9E3319',
        darker: '#89301A',
        darkest: '#791800',
    },
    success: {
        lightest: '#EBF1EA',
        lighter: '#CEE0C8',
        light: '#50CD49',
        primary: '#067000',
        dark: '#305D1D',
        darker: '#2C511D',
        darkest: '#184206',
    },
    info: {
        lightest: '#DADFE7',
        lighter: '#C4CCD9',
        light: '#418AFD',
        primary: '#2555A0',
        dark: '#264A82',
        darker: '#243F6B',
        darkest: '#123366',
    },
    warning: {
        lightest: '#FAF8EA',
        lighter: '#F7E79E',
        light: '#F9DC60',
        primary: '#F9CC09',
        dark: '#665A2A',
        darker: '#584E26',
        darkest: '#534201',
    },
    greys: {
        black: '#000000',
        grey100: '#1b1c20',
        grey90: '#2e2f34',
        grey80: '#3f4149',
        grey70: '#50545e',
        grey60: '#616672',
        grey50: '#7d818b',
        grey40: '#989da5',
        grey30: '#d4d5d8',
        grey20: '#e4e9f2',
        grey10: '#f0f1f2',
        white: '#ffffff',
    },
};
const globalStyleVirtualHelp = {
    bgd: colorsVirtualHelp.greys.white,
    text: colorsVirtualHelp.greys.black,
    fontSize: lightTheme.fontSizes.baseFontSize,
};
const shadowsVirtualHelp = {
    none: 'none',
    small: `0 0.09375rem 0.0625rem 0 ${hexTorgba(colorsVirtualHelp.greys.grey100, 0.15)}`,
    medium: `0 0.5rem 0.85rem 0 ${hexTorgba(colorsVirtualHelp.greys.black, 0.15)}`,
    large: `0 0.75rem 1.875rem 0 ${hexTorgba(colorsVirtualHelp.greys.black, 0.15)}`,
};
const buttonsVirtualHelp = {
    primary: {
        shadow: shadowsVirtualHelp.none,
        static: {
            bgd: colorsVirtualHelp.primary.light,
            border: `0.03125rem solid ${colorsVirtualHelp.primary.darker}`,
            text: colorsVirtualHelp.greys.white,
            shadow: 'none',
        },
        hover: {
            bgd: colorsVirtualHelp.primary.dark,
            border: `0.03125rem solid ${colorsVirtualHelp.primary.darkest}`,
            text: colorsVirtualHelp.greys.white,
            shadow: 'none',
        },
        focus: {
            bgd: colorsVirtualHelp.primary.dark,
            border: `0.03125rem solid ${colorsVirtualHelp.primary.darker}`,
            text: colorsVirtualHelp.greys.white,
            shadow: `0 0 0 0.25rem ${colorsVirtualHelp.primary.lightest}`,
        },
        active: {
            bgd: colorsVirtualHelp.primary.darker,
            border: `0.03125rem solid ${colorsVirtualHelp.greys.black}`,
            text: colorsVirtualHelp.greys.white,
            shadow: `0 0 0 0.25rem ${colorsVirtualHelp.primary.lightest}`,
        },
        selected: {
            bgd: colorsVirtualHelp.primary.light,
            border: `0.03125rem solid ${colorsVirtualHelp.primary.dark}`,
            text: colorsVirtualHelp.greys.white,
            shadow: `none`,
        },
        disabled: {
            bgd: colorsVirtualHelp.greys.white,
            border: `0.03125rem solid ${colorsVirtualHelp.greys.grey10}`,
            text: colorsVirtualHelp.greys.grey40,
            shadow: `none`,
        },
    },
    secondary: {
        shadow: shadowsVirtualHelp.none,
        static: {
            bgd: colorsVirtualHelp.greys.white,
            border: `0.03125rem solid ${colorsVirtualHelp.greys.grey30}`,
            text: colorsVirtualHelp.greys.grey80,
            shadow: 'none',
        },
        hover: {
            bgd: colorsVirtualHelp.greys.grey10,
            border: `0.03125rem solid ${colorsVirtualHelp.greys.grey30}`,
            text: colorsVirtualHelp.greys.grey80,
            shadow: 'none',
        },
        focus: {
            bgd: colorsVirtualHelp.greys.grey10,
            border: `0.03125rem solid ${colorsVirtualHelp.primary.dark}`,
            text: colorsVirtualHelp.greys.grey80,
            shadow: `0 0 0 0.25rem ${colorsVirtualHelp.primary.light}`,
        },
        active: {
            bgd: colorsVirtualHelp.greys.grey20,
            border: `0.03125rem solid ${colorsVirtualHelp.greys.grey40}`,
            text: colorsVirtualHelp.greys.grey80,
            shadow: `0 0 0 0.25rem ${colorsVirtualHelp.primary.dark}`,
        },
        selected: {
            bgd: colorsVirtualHelp.greys.grey10,
            border: `0.03125rem solid ${colorsVirtualHelp.greys.grey30}`,
            text: colorsVirtualHelp.greys.grey80,
            shadow: `0 0 0 0.25rem ${colorsVirtualHelp.primary.dark}`,
        },
        disabled: {
            bgd: colorsVirtualHelp.greys.white,
            border: `0.03125rem solid ${colorsVirtualHelp.greys.grey10}`,
            text: colorsVirtualHelp.greys.grey40,
            shadow: 'none',
        },
    },
    icon: {
        shadow: shadowsVirtualHelp.none,
        static: {
            bgd: 'transparent',
            border: `0.03125rem  solid transparent`,
            text: colorsVirtualHelp.greys.grey80,
            shadow: 'none',
        },
        hover: {
            bgd: colorsVirtualHelp.primary.dark,
            border: `0.03125rem  solid transparent`,
            text: colorsVirtualHelp.greys.white,
            shadow: 'none',
        },
        focus: {
            bgd: 'transparent',
            border: `0.03125rem  solid ${colorsVirtualHelp.primary.darker}`,
            text: colorsVirtualHelp.greys.grey80,
            shadow: `0 0 0 0.25rem ${colorsVirtualHelp.primary.lightest}`,
        },
        active: {
            bgd: colorsVirtualHelp.primary.darker,
            border: `0.03125rem  solid ${colorsVirtualHelp.primary.darker}`,
            text: colorsVirtualHelp.greys.white,
            shadow: `0 0 0 0.25rem ${colorsVirtualHelp.primary.lightest}`,
        },
        selected: {
            bgd: colorsVirtualHelp.primary.light,
            border: `0.03125rem  solid ${colorsVirtualHelp.primary.darker}`,
            text: colorsVirtualHelp.greys.white,
            shadow: 'none',
        },
        disabled: {
            bgd: 'transparent',
            border: 'none',
            text: colorsVirtualHelp.greys.grey40,
            shadow: 'none',
        },
    },
};
const inputsVirtualHelp = {
    bgd: colorsVirtualHelp.greys.white,
    border: `0.03125rem solid ${colorsVirtualHelp.greys.grey30}`,
    borderRadius: lightTheme.radii.default,
    fontColor: colorsVirtualHelp.greys.grey80,
    placeholder: colorsVirtualHelp.greys.grey40,
    shadow: `0 0.0625rem 0.0625rem 0 ${hexTorgba(colorsVirtualHelp.greys.black, 0.1)}`,
    clearBg: colorsVirtualHelp.greys.grey50,
    focus: {
        bgd: colorsVirtualHelp.greys.white,
        border: `solid 0.03125rem ${colorsVirtualHelp.primary.lighter}`,
        shadow: `0 0 0 0.125rem ${colorsVirtualHelp.primary.lightest}`,
    },
    error: {
        border: `0.03125rem solid ${colorsVirtualHelp.error.dark}`,
        fontColor: colorsVirtualHelp.error.primary,
        shadow: `0 0 0 0.125rem ${colorsVirtualHelp.error.light}`,
    },
    checked: {
        bgd: colorsVirtualHelp.primary.main,
        border: `solid 0.03125rem ${colorsVirtualHelp.primary.main}`,
        fontColor: colorsVirtualHelp.greys.white,
        shadow: `0 0.03125rem 0.03125rem 0 ${hexTorgba(colorsVirtualHelp.primary.dark, 0.7)}`,
    },
};
const modalVirtualHelp = {
    bgd: colorsVirtualHelp.greys.white,
    text: colorsVirtualHelp.greys.grey80,
    wrapperBgd: hexTorgba(colorsVirtualHelp.greys.grey50, 0.9),
    titleSize: lightTheme.fontSizes.h5.fontSize,
    titleWeight: 'normal',
    shadow: shadowsVirtualHelp.large,
    border: colorsVirtualHelp.greys.grey30,
};
const popOverVirtualHelp = {
    menuBgd: `${hexTorgba(colorsVirtualHelp.greys.grey10, 0.85)}`,
    menuBorder: `0.5px solid ${colorsVirtualHelp.greys.grey20}`,
    shadow: shadowsVirtualHelp.large,
    itemBgd: 'transparent',
    itemText: colorsVirtualHelp.greys.grey70,
    titleText: colorsVirtualHelp.greys.grey60,
    active: {
        itemBgd: colorsVirtualHelp.primary.dark,
        itemText: colorsVirtualHelp.greys.white,
    },
    disabled: colorsVirtualHelp.greys.grey40,
    separator: `${hexTorgba(colorsVirtualHelp.greys.grey40, 0.3)}`,
};
const notificationVirtualHelp = {
    shadow: shadowsVirtualHelp.large,
    error: {
        text: colorsVirtualHelp.error.lightest,
        closeButton: {
            text: colorsVirtualHelp.error.lighter,
            hover: {
                bgd: colorsVirtualHelp.error.lighter,
                text: colorsVirtualHelp.error.dark,
            },
            active: {
                bgd: colorsVirtualHelp.error.lightest,
                text: colorsVirtualHelp.error.darker,
            },
        },
    },
    success: {
        text: colorsVirtualHelp.success.lightest,
        closeButton: {
            text: colorsVirtualHelp.success.lighter,
            hover: {
                bgd: colorsVirtualHelp.success.lighter,
                text: colorsVirtualHelp.success.dark,
            },
            active: {
                bgd: colorsVirtualHelp.success.lightest,
                text: colorsVirtualHelp.success.darker,
            },
        },
    },
    info: {
        text: colorsVirtualHelp.info.lightest,
        closeButton: {
            text: colorsVirtualHelp.info.lighter,
            hover: {
                bgd: colorsVirtualHelp.info.lighter,
                text: colorsVirtualHelp.info.dark,
            },
            active: {
                bgd: colorsVirtualHelp.info.lightest,
                text: colorsVirtualHelp.info.darker,
            },
        },
    },
    warning: {
        text: colorsVirtualHelp.warning.darker,
        closeButton: {
            text: colorsVirtualHelp.warning.dark,
            hover: {
                bgd: colorsVirtualHelp.warning.dark,
                text: colorsVirtualHelp.greys.white,
            },
            active: {
                bgd: colorsVirtualHelp.warning.darker,
                text: colorsVirtualHelp.greys.white,
            },
        },
    },
};
const linksVirtualHelp = {
    fontColor: colorsVirtualHelp.primary.main,
    fontColorHover: colorsVirtualHelp.primary.dark,
    fontColorActive: colorsVirtualHelp.primary.darker,
    fontColorVisited: colorsVirtualHelp.primary.darkest,
};
const controlBarVirtualHelp = {
    text: colorsVirtualHelp.greys.grey70,
    shadow: shadowsVirtualHelp.large,
    bgd: colorsVirtualHelp.greys.white,
    border: `0.03125rem solid ${colorsVirtualHelp.greys.grey20}`,
    opacity: 1,
    selected: {
        text: buttonsVirtualHelp.primary.selected.text,
        bgd: buttonsVirtualHelp.primary.selected.bgd,
    },
};
const rosterVirtualHelp = {
    title: colorsVirtualHelp.primary.main,
    primaryText: colorsVirtualHelp.greys.grey80,
    secondaryText: colorsVirtualHelp.greys.grey50,
    headerBorder: colorsVirtualHelp.greys.grey40,
    containerBorder: colorsVirtualHelp.greys.grey30,
    bgd: '#f5f7fb',
    fgd: colorsVirtualHelp.greys.white,
    shadow: shadowsVirtualHelp.large,
    maxWidth: '100%',
};
const navbarVirtualHelp = {
    text: colorsVirtualHelp.greys.black,
    bgd: '#f5f7fb',
    headerBorder: colorsVirtualHelp.greys.grey40,
    wrapperBgd: hexTorgba('#f5f7fb', 0.9),
};
const videoGridVirtualHelp = {
    bgd: colorsVirtualHelp.greys.white,
};
const chatBubbleVirtualHelp = {
    incoming: {
        bgd: colorsVirtualHelp.greys.white,
        fontColor: colorsVirtualHelp.greys.grey60,
        linkColor: colorsVirtualHelp.primary.main,
        linkColorHover: colorsVirtualHelp.primary.dark,
        linkColorActive: colorsVirtualHelp.primary.darker,
        linkColorVisited: colorsVirtualHelp.primary.darkest,
    },
    outgoing: {
        bgd: colorsVirtualHelp.primary.main,
        fontColor: colorsVirtualHelp.greys.grey10,
        linkColor: colorsVirtualHelp.greys.white,
        linkColorHover: colorsVirtualHelp.greys.grey10,
        linkColorActive: colorsVirtualHelp.greys.grey20,
        linkColorVisited: colorsVirtualHelp.greys.grey30,
    },
    container: {
        fontColor: colorsVirtualHelp.greys.grey70,
        bgd: colorsVirtualHelp.greys.grey10,
    },
};
const messageAttachmentVirtualHelp = {
    size: {
        fontColor: colorsVirtualHelp.greys.grey40,
        bgd: colorsVirtualHelp.greys.white,
        letterSpacing: '-0.07px',
        lineHight: '16px',
        fontSize: '10.4px',
    },
    icon: {
        bgd: colorsVirtualHelp.greys.grey10,
        color: colorsVirtualHelp.greys.grey80,
    },
    name: {
        fontColor: colorsVirtualHelp.greys.grey80,
    },
    content: {
        letterSpacing: '-0.09px',
        bgd: colorsVirtualHelp.greys.white,
        fontColor: colorsVirtualHelp.greys.grey80,
    },
};
const channelListVirtualHelp = {
    bgd: colorsVirtualHelp.greys.white,
    fontColor: colorsVirtualHelp.greys.grey70,
    border: '1px solid transparent',
    active: {
        bgd: colorsVirtualHelp.primary.dark,
        fontColor: colorsVirtualHelp.greys.white,
    },
    hover: {
        bgd: colorsVirtualHelp.greys.grey10,
    },
    focus: {
        border: `1px solid ${colorsVirtualHelp.primary.dark}`,
        selectedBorder: `1px solid ${colorsVirtualHelp.greys.grey10}`,
    },
    selected: {
        bgd: colorsVirtualHelp.primary.light,
        fontColor: colorsVirtualHelp.greys.white,
    },
    iconButton: {
        activeBgd: colorsVirtualHelp.greys.white,
    },
};
const chatDateHeaderVirtualHelp = {
    bgd: colorsVirtualHelp.greys.grey60,
    fontColor: colorsVirtualHelp.greys.white,
};

const chatVirtualhelpTheme = {
    title: colorsVirtualHelp.greys.grey100,
    primaryText: colorsVirtualHelp.greys.grey80,
    secondaryText: colorsVirtualHelp.greys.grey50,
    headerBorder: colorsVirtualHelp.greys.grey40,
    containerBorder: colorsVirtualHelp.greys.grey30,
    bgd: colorsVirtualHelp.greys.white,
    fgd: colorsVirtualHelp.greys.black,
    shadow: lightThemeShadows.large,
    maxWidth: '20.5rem',
}

const meetingInfoVirtualhelpTheme = {
    title: colorsVirtualHelp.primary.main,
    primaryText: colorsVirtualHelp.greys.grey80,
    secondaryText: colorsVirtualHelp.greys.grey50,
    headerBorder: colorsVirtualHelp.greys.grey40,
    containerBorder: colorsVirtualHelp.greys.grey30,
    bgd: '#f5f7fb',
    fgd: colorsVirtualHelp.greys.white,
    shadow: shadowsVirtualHelp.large,
    maxWidth: '20.5rem',
    
}

export const chatVirtualHelpTheme = {
    ...lightTheme,
    name : 'Virtual Help Theme',
    chat: chatVirtualhelpTheme,
    meetingInfo: meetingInfoVirtualhelpTheme,
    colors: colorsVirtualHelp,
    globalStyle: globalStyleVirtualHelp,
    chatDateHeader: chatDateHeaderVirtualHelp,
    channelList: channelListVirtualHelp,
    messageAttachment: messageAttachmentVirtualHelp,
    chatBubble: chatBubbleVirtualHelp,
    videoGrid: videoGridVirtualHelp,
    navbar: navbarVirtualHelp,
    roster: rosterVirtualHelp,
    controlBar: controlBarVirtualHelp,
    links: linksVirtualHelp,
    notification: notificationVirtualHelp,
    popOver: popOverVirtualHelp,
    modal: modalVirtualHelp,
    inputs: inputsVirtualHelp,
    buttons: buttonsVirtualHelp,
    shadows: shadowsVirtualHelp
};
